import dayjs from 'dayjs';
import { createEvent, EventAttributes } from 'ics';
import _keyBy from 'lodash/keyBy';
import { customAlphabet, nanoid } from 'nanoid';
import { alphanumeric } from 'nanoid-dictionary';
import qs from 'qs';
import { browserVersion, isAndroid, isChrome, isDesktop, isIOS, isSafari, osVersion } from 'react-device-detect';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import {
  scrollIntoView as scrollIntoViewWithPolyfill,
  scrollTo as scrollToWithPolyfill
} from 'seamless-scroll-polyfill';

import config from 'config';

import {
  AppStorage,
  CallbackTriggers,
  EventSchema,
  EventUserSchema,
  InviteeSchema,
  OnStageUser,
  Payme,
  PaymentType,
  PricingPlanFeature,
  PricingPlanInterval,
  StreamingPlatform,
  UserAction,
  UserActionType,
  UserRole,
  UserSession
} from 'types';
import { TWITCH_AUTH_BASE_URL } from 'utils';
import { api, storage } from 'services';

import { PALETTE_COLORS } from './constants';

export const getUserSession = (cognitoUser): UserSession => {
  let userSession: UserSession = {
    provider: '',
    username: '',
    image: '',
    name: '',
    accessToken: '',
    email: '',
    phoneNumber: ''
  };

  if (cognitoUser.username?.toLowerCase()?.includes('google')) {
    userSession = getUserSessionFromGoogle(cognitoUser);
  } else if (cognitoUser.username?.toLowerCase()?.includes('facebook')) {
    userSession = getUserSessionFromFacebook(cognitoUser);
  } else {
    userSession = getUserSessionWithPhoneNumber(cognitoUser);
  }

  return userSession;
};

export const getUserSessionFromGoogle = (cognitoUser): UserSession => ({
  provider: 'google',
  username: cognitoUser.username,
  name: cognitoUser.attributes.name,
  image: cognitoUser.attributes.picture,
  email: cognitoUser.attributes.email,
  accessToken: cognitoUser.signInUserSession.accessToken.jwtToken
});

export const getUserSessionFromFacebook = (cognitoUser): UserSession => ({
  provider: 'facebook',
  username: cognitoUser.username,
  name: cognitoUser.attributes.name,
  image: JSON.parse(cognitoUser.attributes.picture).data.url,
  email: cognitoUser.attributes.email,
  accessToken: cognitoUser.signInUserSession.accessToken.jwtToken
});

export const getUserSessionWithPhoneNumber = (cognitoUser): UserSession => ({
  provider: 'phoneNumber',
  username: cognitoUser.username,
  name: cognitoUser.attributes.name,
  phoneNumber: cognitoUser.attributes.phone_number,
  accessToken: cognitoUser.signInUserSession.accessToken.jwtToken
});

export const catchAsync = promise =>
  promise.then(data => ({ success: true, data })).catch(error => Promise.resolve({ success: false, error }));

export const getPaletteColor = (index: number) => PALETTE_COLORS[index % PALETTE_COLORS.length];

export const choosePaletteColor = (id?: string, order?: number) => {
  if (order !== undefined) return getPaletteColor(order);

  const uid = id ? hashUsername(id).toString() : null;
  const index = uid ? parseInt(uid, 10) : generateRandomPositiveNumber(10);

  return getPaletteColor(index);
};

// Example Lighten: shadeColor("#63C6FF",  40);
// Example Darken:  shadeColor("#63C6FF", -40);
// SOURCE: https://stackoverflow.com/a/13532993/9419218
export const shadeColor = (color: string, percent: number) => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = Math.floor((R * (100 + percent)) / 100);
  G = Math.floor((G * (100 + percent)) / 100);
  B = Math.floor((B * (100 + percent)) / 100);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR = R.toString(16).length === 1 ? `0${R.toString(16)}` : R.toString(16);
  const GG = G.toString(16).length === 1 ? `0${G.toString(16)}` : G.toString(16);
  const BB = B.toString(16).length === 1 ? `0${B.toString(16)}` : B.toString(16);

  return `#${RR}${GG}${BB}`;
};

export const generateRandomPositiveNumber = (range: number): number => Math.floor(Math.random() * range);
// TODO: cover util functions with unit tests
export const getFirstLetters = (words: string[]): string => words.map(word => word[0].toUpperCase()).join('');
export const sumOfDigits = (number: number): number =>
  number
    .toString()
    .split('')
    .map(Number)
    .reduce((a, b) => a + b, 0);

export const mapStreamingPlatformToUserAction = (platformKey: StreamingPlatform): UserAction | null => {
  switch (platformKey) {
    case StreamingPlatform.FACEBOOK:
      return { type: UserActionType.PUSH_STREAM_TO_FACEBOOK };
    case StreamingPlatform.TWITCH:
      return { type: UserActionType.PUSH_STREAM_TO_TWITCH };
    case StreamingPlatform.YOUTUBE:
      return { type: UserActionType.PUSH_STREAM_TO_YOUTUBE };
    default:
      return null;
  }
};

export const getPlatformNameByKey = (platformKey: StreamingPlatform): string | null => {
  switch (platformKey) {
    case StreamingPlatform.YOUTUBE:
      return 'Youtube';

    case StreamingPlatform.FACEBOOK:
      return 'Facebook';

    case StreamingPlatform.TWITCH:
      return 'Twitch';

    default:
      return null;
  }
};

// Source: https://stackoverflow.com/a/5647103
export const parseLocationHash = (locationHash: string = '') => {
  const querystring = locationHash
    .slice(2)
    .split('&')
    .map(v => v.split('='))
    .reduce((pre, [key, value]) => ({ ...pre, [key]: value }), {});

  const parsedValue = qs.parse(querystring);

  return parsedValue;
};

export const getRedirectURL = () =>
  `${window.location.protocol}//${window.location.hostname}${window.location.port}${window.location.pathname}`;

// TODO: redirect works wrong when twitch native mobile application is installed
// This util helps to handle oauth response
// It does the following action
//  - opens a new blank tab
//  - listens for URL hash every 1000 ms
//  - if it catches the `access_token` field in URL hash, it resolves the promise with access_token and closes the opened tab
export const authorizeTwitch = ({ clientId, scope = [] }: { clientId: string; scope: string[] }) =>
  new Promise<string>((resolve, reject) => {
    const baseUrl = TWITCH_AUTH_BASE_URL;
    const params = {
      client_id: clientId,
      response_type: 'token',
      scope: scope.join(' '),
      redirect_uri: getRedirectURL(),
      force_verify: true
    };
    const fullUrl = baseUrl + qs.stringify(params, { addQueryPrefix: true });
    const OAuthWindow = window.open(fullUrl, '_blank', 'height=570,width=520');

    if (OAuthWindow) {
      const intervalId = setInterval(() => {
        let parsedHashObject: { access_token?: string } = {};

        try {
          parsedHashObject = parseLocationHash(OAuthWindow.location.hash);
        } catch (e) {
          console.warn(e);
        }

        if (parsedHashObject?.access_token) {
          OAuthWindow.close();
          resolve(parsedHashObject.access_token as string);
        }
      }, 1000);

      OAuthWindow.opener = window;
      OAuthWindow.sessionStorage.setItem('ignoreCognitoRedirect', 'true');
      OAuthWindow.onbeforeunload = () => {
        clearInterval(intervalId);
      };
    }
  });

// Source: https://stackoverflow.com/a/22429679
export const hashUsername = (str: string, seed: number = 0): number => {
  const l = str.length;
  let hval = seed === 0 ? 0x811c9dc5 : seed;

  for (let i = 0; i < l; i++) {
    // eslint-disable-next-line no-bitwise
    hval ^= str.charCodeAt(i);

    hval +=
      // eslint-disable-next-line no-bitwise
      (hval << 1) + (hval << 4) + (hval << 7) + (hval << 8) + (hval << 24);
  }

  // eslint-disable-next-line no-bitwise
  return hval >>> 0;
};

export const convertInviteeToEventUser = (invitee: InviteeSchema): EventUserSchema => ({
  username: invitee.inviteeCode,
  name: invitee.name,
  role: invitee.role,
  member: false,
  avatar: '',
  invitee: true,
  eventCode: invitee.eventCode,
  inviteeCode: invitee.inviteeCode,
  timestamp: '',
  joinedOnStageDate: ''
});

// Check for OS and browser support
export const hasFullSupport = () => {
  const isChromeSupported = isChrome && parseInt(browserVersion, 10) >= 58;
  const isIosSupported = isIOS && parseFloat(osVersion) >= 12.2;

  if (isDesktop) {
    return true;
  }

  if (isIosSupported) {
    if (isChromeSupported || isSafari) return true;
  }
  if (isAndroid && isChromeSupported) return true;

  return false;
};

export const refreshTokenForStreamingPlatform = async (
  platformKey: StreamingPlatform
): Promise<{
  platformKey: StreamingPlatform;
  token: { accessToken: string; expiresAt: number };
}> => {
  switch (platformKey) {
    case StreamingPlatform.YOUTUBE: {
      const { accessToken, expiresAt } = await api.requests.youtube.refreshToken();

      return { platformKey: StreamingPlatform.YOUTUBE, token: { accessToken, expiresAt } };
    }

    case StreamingPlatform.FACEBOOK: {
      const { accessToken, expiresAt } = { accessToken: '', expiresAt: 0 };

      return { platformKey: StreamingPlatform.YOUTUBE, token: { accessToken, expiresAt } };
    }

    case StreamingPlatform.TWITCH: {
      const { accessToken, expiresAt } = { accessToken: '', expiresAt: 0 };

      return { platformKey: StreamingPlatform.YOUTUBE, token: { accessToken, expiresAt } };
    }

    default:
      throw Error('platformKey not found');
  }
};

export const convertEventUserToStageUser = (eventUser: EventUserSchema): OnStageUser => ({
  username: eventUser.username,
  image: eventUser.avatar,
  name: eventUser.name,
  role: eventUser.role === UserRole.MODERATOR ? UserRole.MODERATOR : UserRole.SPEAKER,
  member: !!eventUser.member,
  joined: false,
  type: 'pending'
});

export const convertInviteeToStageUser = (invitee: InviteeSchema): OnStageUser => ({
  ...convertEventUserToStageUser(convertInviteeToEventUser(invitee)),
  type: 'invitee'
});

export const scrollIntoView = (target: HTMLElement, options?: ScrollIntoViewOptions) => {
  scrollIntoViewWithPolyfill(target, { behavior: 'smooth', ...options });
};

export const scrollTo = (target: Element | (Window & typeof globalThis), options?: ScrollToOptions) =>
  scrollToWithPolyfill(target, { behavior: 'smooth', ...options });

export const getActualUsers = (eventUsers: EventUserSchema[], rtmMemberIds: string[]) => {
  const UIDMap = _keyBy(rtmMemberIds);
  const actualUsers = eventUsers.filter(user => {
    const viewerUID = hashUsername(user.username).toString();

    return !!UIDMap[viewerUID];
  });

  return actualUsers;
};

export const getActualRequests = (requests: EventUserSchema[], rtmMemberIds: string[]) => {
  const UIDMap = _keyBy(rtmMemberIds);
  const actualRequests = requests.filter(request => {
    const viewerUID = hashUsername(request.username).toString();

    return !!UIDMap[viewerUID];
  });

  return actualRequests;
};

// SOURCE: https://stackoverflow.com/a/46181/9419218
export const validateEmail = (email: string) =>
  !!String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

export const validatePassword = (password: string) => {
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);

  if (!hasUpperCase || !hasLowerCase) {
    return false;
  }
  return true;
};

export const arrayToObject = <T>(data: T[], key: string) => {
  const result: { [key: string]: T } = {};

  data.forEach(item => {
    if (item[key]) result[item[key]] = item;
  });

  return result;
};

export const getCallbackTriggersMap = (triggers: CallbackTriggers) => arrayToObject(triggers, 'type');

export const getChannelURL = (channelName: string, full: boolean = false) =>
  full ? `${window.location.origin}/${channelName}` : `/${channelName}`;

export const getEventURL = (channelName: string, eventCode: string, full: boolean = false) =>
  `${getChannelURL(channelName, full)}/${eventCode}`;

export const getStripeReturnURL = (callbackData: string, paymentType: PaymentType) => {
  switch (paymentType) {
    case PaymentType.CREATOR:
      return `${config.app.url}/payment${qs.stringify(
        {
          type: PaymentType.CREATOR,
          pricingPlanId: callbackData
        },
        { addQueryPrefix: true }
      )}`;
    case PaymentType.MEMBER:
      return `${config.app.url}/payment${qs.stringify(
        {
          type: PaymentType.MEMBER,
          channelName: callbackData
        },
        { addQueryPrefix: true }
      )}`;

    default:
      return '';
  }
};

export const getCalendarFileName = (eventCode: string) => `LTV_${eventCode}.ics`;

// this implementation does not work for iOS chrome, chromium has a bug which is not fixed yet https://stackoverflow.com/a/45814212
// TODO: track the chromium bug, test it in ios chrome and fix it
export const generateCalendarEvent = (event: EventSchema) =>
  new Promise<Blob>((resolve, reject) => {
    const startDate = dayjs(event.startDate);
    const endDate = startDate.add(1, 'hour');

    const startYear = startDate.get('year');
    // starts with 0
    // SOURCE: https://day.js.org/docs/en/get-set/get
    const startMonth = startDate.get('month') + 1;
    const startDay = startDate.get('date');
    const startHour = startDate.get('hour');
    const startMinute = startDate.get('minute');

    const endYear = endDate.get('year');
    // starts with 0
    // SOURCE: https://day.js.org/docs/en/get-set/get
    const endMonth = endDate.get('month') + 1;
    const endDay = endDate.get('date');
    const endHour = endDate.get('hour');
    const endMinute = endDate.get('minute');

    const eventURL = getEventURL(event.channelName, event.code, true);

    // SOURCE: https://github.com/adamgibbons/ics#attributes
    const calendarAttributes: EventAttributes = {
      productId: 'LTV',
      uid: event.code,
      start: [startYear, startMonth, startDay, startHour, startMinute],
      end: [endYear, endMonth, endDay, endHour, endMinute],
      title: event.name,
      description: `${event.description}\n\n${eventURL}`,
      url: eventURL
    };

    return createEvent(calendarAttributes, (error, value) => {
      if (!error) {
        const blob = new Blob([value], { type: 'text/calendar' });

        resolve(blob);
      } else {
        reject(error);
      }
    });
  });

export const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const downloadLink = document.createElement('a');

  downloadLink.href = url;
  downloadLink.download = filename;

  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

// built-n Number.prototype.toFixed methods rounds up the floating values
// SOURCE: https://stackoverflow.com/a/11818658/9419218
export const toFixed = (num: number, fixed: number): string => {
  const re = new RegExp(`^-?\\d+(?:\.\\d{0,${fixed || -1}})?`);

  return num.toString().match(re)?.[0] || num.toString();
};

// SOURCE: https://stackoverflow.com/a/9462382/9419218
export const nFormatter = (number: number, digits: number = 0) => {
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' }
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = lookup
    .slice()
    .reverse()
    .find(({ value }) => number >= value);

  return item ? toFixed(number / item.value, digits).replace(rx, '$1') + item.symbol : '0';
};

export const displayCount = (count: number): string => {
  if (count < 1e3) return `${count}`;
  if (count < 1e5) return `${nFormatter(count, 1)}`;
  if (count < 1e6) return `${nFormatter(count, 0)}`;
  if (count < 1e8) return `${nFormatter(count, 2)}`;

  return nFormatter(count, 0);
};

export const displayPrice = (cents: number): string => {
  const priceInDollars = cents / 100;

  if (cents % 100 !== 0) {
    return priceInDollars.toFixed(2);
  }
  return priceInDollars.toString();
};

export const mapPricingPlanFeature = (feature: PricingPlanFeature) => {
  switch (feature) {
    case PricingPlanFeature.PUBLIC_EVENT:
      return 'Public events';
    case PricingPlanFeature.MEMBERS_ONLY_EVENT:
      return 'Members only events';
    case PricingPlanFeature.RECORD_VIDEO:
      return 'Record video';

    default:
      return feature;
  }
};

export const mapPricingPlanInterval = (interval: PricingPlanInterval) => {
  switch (interval) {
    case PricingPlanInterval.WEEK:
      return 'Week';
    case PricingPlanInterval.MONTH:
      return 'Month';
    case PricingPlanInterval.QUARTER:
      return 'Quarter';
    case PricingPlanInterval.HALF_YEAR:
      return 'Half year';
    case PricingPlanInterval.YEAR:
      return 'Year';

    default:
      return interval;
  }
};

// SOURCE: https://github.com/iamkun/dayjs/issues/328#issuecomment-418713656
export const sortEventUsersByDate = (eventUsers: EventUserSchema[] = []): EventUserSchema[] => {
  if (!eventUsers || eventUsers.length === 0) return [];

  return [...eventUsers].sort((a, b) => (dayjs(a.joinedOnStageDate).isAfter(dayjs(b.joinedOnStageDate)) ? 1 : -1));
};

export const generatePaymeOrderId = () => customAlphabet(alphanumeric)(20);

export const generateRandomString = () => nanoid();

export const getIVSIngestServerUrl = (ingestServer: string) => `rtmps://${ingestServer}:443/app/`;

export const getIVSIngestUrl = (ingestServer?: string | null, streamKey?: string | null): string | undefined =>
  ingestServer && streamKey ? `${getIVSIngestServerUrl(ingestServer)}${streamKey}` : undefined;

// SOURCE: https://stackoverflow.com/a/33366171/9419218
export const deleteAllCookies = () => {
  const cookies = document.cookie.split('; ');

  for (let c = 0; c < cookies.length; c++) {
    const d = window.location.hostname.split('.');

    while (d.length > 0) {
      const cookieBase = `${encodeURIComponent(
        cookies[c].split(';')[0].split('=')[0]
      )}=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=${d.join('.')} ;path=`;
      const p = window.location.pathname.split('/');

      document.cookie = `${cookieBase}/`;
      while (p.length > 0) {
        document.cookie = cookieBase + p.join('/');
        p.pop();
      }
      d.shift();
    }
  }
};

export const revalidateAppCache = () => {
  deleteAllCookies();

  Object.entries(AppStorage.Local).forEach(([name, key]) => {
    storage.local.remove(key);
  });

  Object.entries(AppStorage.Session).forEach(([name, key]) => {
    storage.session.remove(key);
  });

  storage.local.set(AppStorage.Local.VERSION, config.app.version);
};

// SOURCE: https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
export const isValidURL = (str?: string) => {
  if (!str) {
    return false;
  }

  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$', // fragment locator
    'i'
  );

  return !!pattern.test(str);
};

export const displayPriceAndInterval = (price: number, interval: PricingPlanInterval) =>
  `$${displayPrice(price)} per ${mapPricingPlanInterval(interval)}`.toLocaleLowerCase();

export const constructPaymeCheckoutURL = (params: object) => {
  const { checkoutUrl } = config.payme;
  const stringifiedParams = qs.stringify(params, { delimiter: ';', encode: false });
  const encodedParams = btoa(stringifiedParams);
  const constructedURL = `${checkoutUrl}/${encodedParams}`;

  return constructedURL;
};

/**
 *
 * @param fee amount in "sums"
 * @returns amount in "tiyins"
 */
export const getPaymeCheckoutAmount = (fee: number) => fee * 100;

export const validatePhoneNumber = (phoneNumber: string): boolean => isPossiblePhoneNumber(phoneNumber, undefined);

export const redirectToPaymeCheckout = (args: {
  amount: number;
  callbackUrl: string;
  attributes: {
    username: string;
    name: string;
    channelName: string;
  };
}) => {
  const params = {
    m: config.payme.merchantId,
    a: getPaymeCheckoutAmount(args.amount),
    c: args.callbackUrl,
    l: Payme.Language.UZ,
    cr: Payme.Currency.UZS,
    'ac.order': generatePaymeOrderId(),
    'ac.username': args.attributes.username,
    'ac.name': args.attributes.name,
    'ac.channel': args.attributes.channelName
  };
  const constructedURL = constructPaymeCheckoutURL(params);

  // @ts-ignore
  window.location = constructedURL;
};

export const getUserInitials = (name: string) => name.trim()[0];
